import * as React from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

/**
 * Syncs a `<Tabs>` active value to a URL query param (default `?tab=`) so the
 * selected tab survives reload and browser back/forward. The default tab is
 * represented by an absent param to keep the URL clean.
 *
 * Switching tabs also clears `page`, so per-tab tables don't inherit each
 * other's pagination offset (e.g. landing on page 3 of an unrelated table).
 *
 * @returns `[value, setValue]` — wire to `<Tabs value=… onValueChange=…>`.
 */
export function useTabParam(defaultValue: string, key = "tab") {
  const nav = useNavigate();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();

  const value = searchParams.get(key) ?? defaultValue;

  const setValue = React.useCallback(
    (v: string) => {
      const sp = new URLSearchParams(searchParams.toString());
      if (v === defaultValue) sp.delete(key);
      else sp.set(key, v);
      sp.delete("page");
      const qs = sp.toString();
      nav(qs ? `${pathname}?${qs}` : pathname, { replace: true });
    },
    [searchParams, pathname, nav, key, defaultValue],
  );

  return [value, setValue] as const;
}
